/**
 * Ladezustand einer Liste, die nach Änderungen erneut abgerufen wird.
 *
 * Nur der **erste** Abruf meldet „lädt". Jeder weitere lässt stehen, was da ist, und tauscht
 * den Inhalt aus, sobald er da ist. Der Grund ist nicht Ästhetik: `BaseTable` ersetzt bei
 * `loading` die gesamte Tabelle durch einen Hinweis und `PageTemplate` den gesamten
 * Seiteninhalt durch den Spinner. Wer in diesem Moment klickt, klickt ins Leere – die Zeile
 * unter dem Mauszeiger verschwindet zwischen Maustaste-runter und -hoch, der Klick kommt nie
 * an. In einer Tabelle mit Inline-Bearbeitung heißt das: Das Speichern beim Verlassen des
 * Feldes stiehlt genau den Klick, der in die nächste Zeile wechseln soll.
 *
 * Ein Ladefehler ist davon unberührt – der gehört weiter in das `error` des Data-Hooks.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface LoadState {
  /** Wahr nur, solange der erste Abruf zum aktuellen Schlüssel läuft */
  loading: boolean;
  /** Vor dem Abruf aufrufen */
  ladenStarten: () => void;
  /** Nach dem Abruf aufrufen, auch im Fehlerfall (also im `finally`) */
  ladenBeenden: () => void;
}

/**
 * @param schluessel Woran die Liste hängt – etwa die ID, deren Kindzeilen sie zeigt. Wechselt
 *   er, gilt der nächste Abruf wieder als der erste: Die Zeilen gehören dann zu einem anderen
 *   Datensatz und dürfen nicht stehen bleiben. Ohne Schlüssel ist es der erste Abruf überhaupt.
 */
export function useLoadState(schluessel?: string | null): LoadState {
  const [loading, setLoading] = useState(true);
  const ersterAbruf = useRef(true);

  useEffect(() => {
    ersterAbruf.current = true;
  }, [schluessel]);

  const ladenStarten = useCallback(() => {
    if (ersterAbruf.current) {
      setLoading(true);
    }
  }, []);

  const ladenBeenden = useCallback(() => {
    setLoading(false);
    ersterAbruf.current = false;
  }, []);

  return { loading, ladenStarten, ladenBeenden };
}
